import { AxiosResponse } from "axios";
import { IDataResponse, IResponse } from "../../interfaces/response/IResponse";
import { INoteAction } from "../../interfaces/supplierGeography/INoteAction";
import useAxios from "../../../hooks/useAxios/useAxios";
import { useCallback } from "react";
import { IGridResultResponse } from "../../interfaces/response/IGridResultResponse";
import { ISelectTreeModel } from "../../interfaces/shared/ISelectTreeModel";
import { ISendEmailsToSPGetAction } from "../../interfaces/supplierGeography/ISendEmailsToSPGetAction";
import { ISendEmailsToSPPostAction } from "../../interfaces/supplierGeography/ISendEmailsToSPPostAction";
import { IChangeLangGetAction } from "../../interfaces/supplierGeography/IChangeLangGetAction";
import { IUnsubscribeEmailNotificationPostAction } from "../../interfaces/supplierGeography/IUnsubscribeEmailNotificationPostAction";
import { IUnsubscribeFormGetAction } from "../../interfaces/supplierGeography/IUnsubscribeFormGetAction";
import { IUnsubscribeFormPostAction } from "../../interfaces/supplierGeography/IUnsubscribeFormPostAction";

export const useGetChangeSGNoteData = (): {
    isLoading: boolean,
    cancel: () => void,
    getChangeNoteData: (id: string) => Promise<AxiosResponse<IDataResponse<INoteAction>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IDataResponse<INoteAction>>({
        method: 'get',
    });

    const getChangeNoteData = useCallback(async (id: string) => await execute(undefined, {
        url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getSGNoteData?id=${id}`
    }), [execute]);

    return {getChangeNoteData, isLoading, cancel};
}


export const useSaveNote = (): {
    isLoading: boolean,
    cancel: () => void,
    saveNote: (params: INoteAction) => Promise<AxiosResponse<IResponse> | undefined>;
} => {
    const {execute, isLoading, cancel} = useAxios<IResponse, INoteAction>({
        method: 'POST',
        url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/saveSGNoteData`
    });

    const saveNote = useCallback(async (params: INoteAction) => await execute(params), [execute]);

    return {saveNote, isLoading, cancel};
};

export const useGetSGEquipmentsTree = (): {
    isLoading: boolean,
    cancel: () => void,
    getSGEquipmentsTree: (filter: string | null, skip?: number, take?: number) => Promise<AxiosResponse<IGridResultResponse<ISelectTreeModel>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IGridResultResponse<ISelectTreeModel>>({
        method: 'get',
    });

    const getSGEquipmentsTree = useCallback(async (filter: string | null, skip?: number, take?: number) => {
        let q = `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getSGEquipmentsTree`;

        if (filter && filter !== '') {
            q += `?filter=${filter}`;

            if (skip !== undefined && skip >= 0 && take !== undefined && take >= 0) {
                q += `&skip=${skip}&take=${take}`;
            }
        } else {
            if (skip !== undefined && skip >= 0 && take !== undefined && take >= 0) {
                q += `?skip=${skip}&take=${take}`;
            }
        }

        return await execute(undefined, {
            url: q
        });
    }, [execute]);

    return {getSGEquipmentsTree, isLoading, cancel};
};

export const useGetSendEmailsToSPData = (): {
    isLoading: boolean,
    cancel: () => void,
    getSendEmailsToSPData: (id: string | Array<string>) => Promise<AxiosResponse<IDataResponse<ISendEmailsToSPGetAction>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IDataResponse<ISendEmailsToSPGetAction>>({
        method: 'get',
    });

    const getSendEmailsToSPData = useCallback(async (id: string | Array<string>) => {
       let param: string;

        if (Array.isArray(id)) {
            param = id.join(';');
        } else {
            param = id;
        }

        return await execute(undefined, {
            url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getSendEmailsToSPData?id=${param}`
        });
    }, [execute]);

    return {getSendEmailsToSPData, isLoading, cancel};
}

export const usePostSendEmailsToSPData = (): {
    isLoading: boolean,
    cancel: () => void,
    postSendEmailsToSP: (params: ISendEmailsToSPPostAction) => Promise<AxiosResponse<IResponse> | undefined>;
} => {
    const {execute, isLoading, cancel} = useAxios<IResponse, ISendEmailsToSPPostAction>({
        method: 'POST',
        url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/postSendEmailsToSPAction`
    });

    const postSendEmailsToSP = useCallback(async (params: ISendEmailsToSPPostAction) => await execute(params), [execute]);

    return {postSendEmailsToSP, isLoading, cancel};
};

export const useGetChangeLangData = (): {
    isLoading: boolean,
    cancel: () => void,
    getChangeLangData: (replyTo: string, lang: string) => Promise<AxiosResponse<IDataResponse<IChangeLangGetAction>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IDataResponse<IChangeLangGetAction>>({
        method: 'get',
    });

    const getChangeLangData = useCallback(async (replyTo: string, lang: string) => {

        return await execute(undefined, {
            url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getChangeLangData?replyTo=${replyTo}&lang=${lang}`
        });
    }, [execute]);

    return {getChangeLangData, isLoading, cancel};
};

export const usePostUnsubscribeEmailNotificationData = (): {
    isLoading: boolean,
    cancel: () => void,
    postUnsubscribeEmailNotification: (params: IUnsubscribeEmailNotificationPostAction) => Promise<AxiosResponse<IResponse> | undefined>;
} => {
    const {execute, isLoading, cancel} = useAxios<IResponse, IUnsubscribeEmailNotificationPostAction>({
        method: 'POST',
        url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/postUnsubscribeEmailNotification`
    });

    const postUnsubscribeEmailNotification = useCallback(async (params: IUnsubscribeEmailNotificationPostAction) => await execute(params), [execute]);

    return {postUnsubscribeEmailNotification, isLoading, cancel};
};

export const useGetUnsubscribeFormData = (): {
    isLoading: boolean;
    cancel: () => void;
    getUnsubscribeFormData: (id: string | Array<string>) => Promise<AxiosResponse<IDataResponse<IUnsubscribeFormGetAction>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IDataResponse<IUnsubscribeFormGetAction>>({
        method: 'get',
    });

    const getUnsubscribeFormData = useCallback(async (id: string | Array<string>) => {

        let param: string;

        if (Array.isArray(id)) {
            param = id.join(';');
        } else {
            param = id;
        }

        return await execute(undefined, {
            url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getUnsubscribeFormData?id=${param}`
        });
    }, [execute]);

    return {getUnsubscribeFormData, isLoading, cancel};
}

export const usePostUnsubscribeForm = (): {
    isLoading: boolean,
    cancel: () => void,
    postUnsubscribeForm: (params: IUnsubscribeFormPostAction) => Promise<AxiosResponse<IResponse> | undefined>;
} => {
    const {execute, isLoading, cancel} = useAxios<IResponse, IUnsubscribeFormPostAction>({
        method: 'POST',
        url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/postUnsubscribeFormAction`
    });

    const postUnsubscribeForm = useCallback(async (params: IUnsubscribeFormPostAction) => await execute(params), [execute]);

    return {postUnsubscribeForm, isLoading, cancel};
};

export const useGetSGLogsEmailBody = (): {
    isLoading: boolean;
    cancel: () => void;
    getSGLogsEmailBody: (id: string) => Promise<AxiosResponse<IDataResponse<string>> | undefined>
} => {
    const {execute, isLoading, cancel} = useAxios<IDataResponse<string>>({
        method: 'get',
    });

    const getSGLogsEmailBody = useCallback(async (id: string) => {

        return await execute(undefined, {
            url: `api/${process.env.REACT_APP_API_VERSION}/supplierGeography/getSGLogsEmailBody?id=${id}`
        });
    }, [execute]);

    return {getSGLogsEmailBody, isLoading, cancel};
}
